///
/// EarthBrowser
///
/// Synoptic Weather legend body: pressure centres, the isobar interval
/// currently in use (the shader picks it from the zoom level, so without
/// this the user has no way to know what the lines mean), front symbols
/// while the fronts sub-layer is on, and the jet stream speed scale while
/// the jet sub-layer is on. Colors mirror PressureCenterNode, FrontNode
/// and jetColor() in shaders/jetstream_sphere.glsl.
///
/// Created by Matt Giger on 10/23/2026
/// Copyright (c) 2026 Geodata Labs
///

import { wgs } from "@/earthbrowser/utils/geographic"
import { useAppSelector } from "@/redux/store"
import { ColorScale, LegendFootnote, LegendSection, SymbolRow, gradientFromStops } from "./LegendPrimitives"

// Mirrors the interval ladder in pressure_isolines_sphere.glsl: the ideal
// interval is c_intervalPerTexel (5) hPa per source texel spanned by one
// screen pixel, snapped to a power of two in [1, 8]
const cIntervalPerTexel = 5
const cTexelKm = 0.25 * 111.32
const cAssumedFovDeg = 45
const cAssumedViewportPx = 900

function isobarIntervalHpa(cameraRadiusKm: number): number {
	const altitudeKm = Math.max(1, cameraRadiusKm - wgs.a)
	const kmPerPx = 2 * altitudeKm * Math.tan(cAssumedFovDeg * 0.5 * Math.PI / 180) / cAssumedViewportPx
	const ideal = Math.min(8, Math.max(1, kmPerPx / cTexelKm * cIntervalPerTexel))
	return Math.pow(2, Math.round(Math.log2(ideal)))
}

// Jet scale stops from jetColor(): [m/s, css color]
const cJetStops: Array<[number, string]> = [
	[25, "rgb(179, 212, 242)"],
	[50, "rgb(219, 242, 255)"],
	[75, "rgb(255, 255, 255)"],
	[100, "rgb(255, 235, 189)"],
]
const cJetGradient = gradientFromStops(cJetStops)

/** Rounded to 10 kt; the open-ended top stop rounds up */
function toKnots(mps: number, roundUp: boolean = false): number {
	const kt = mps * 1.943844 / 10
	return (roundUp ? Math.ceil(kt) : Math.round(kt)) * 10
}

const cJetTicks = cJetStops.map(([mps], i) => {
	const last = i === cJetStops.length - 1
	return { at: (i / (cJetStops.length - 1)) * 100, label: `${toKnots(mps, last)}${last ? "+ kt" : ""}` }
})

export function SynopticLegendContent() {
	const visibleDatasets = useAppSelector(state => state.earthbrowser.visibleDatasets)
	const cameraRadius = useAppSelector(state => state.earthbrowser.cameraState.radius)
	const frontsVisible = visibleDatasets.includes("fronts")
	const jetVisible = visibleDatasets.includes("jetstream")
	const upperVisible = visibleDatasets.includes("upper_air")
	const interval = isobarIntervalHpa(cameraRadius)

	return (
		<>
			<SymbolRow
				swatch={
					<span className="flex gap-1 font-bold text-sm leading-none">
						<span style={{ color: "rgb(255, 51, 51)" }}>L</span>
						<span style={{ color: "rgb(51, 128, 255)" }}>H</span>
					</span>
				}
			>
				Pressure centres (hPa)
			</SymbolRow>
			<SymbolRow swatch={<IsobarSwatch />}>Isobars about every {interval} hPa</SymbolRow>

			{upperVisible && (
				<>
					<SymbolRow swatch={<IsobarSwatch core="rgb(255, 232, 173)" />}>500 hPa heights (dam), lifted</SymbolRow>
					<SymbolRow swatch={<AxisSwatch />}>Trough (dashed) · ridge (zigzag)</SymbolRow>
				</>
			)}

			{frontsVisible && (
				<LegendSection>
					<SymbolRow swatch={<FrontSwatch kind="cold" />}>Cold front</SymbolRow>
					<SymbolRow swatch={<FrontSwatch kind="warm" />}>Warm front</SymbolRow>
					<SymbolRow swatch={<FrontSwatch kind="stationary" />}>Stationary front</SymbolRow>
					<SymbolRow swatch={<FrontSwatch kind="occluded" />}>Occluded front</SymbolRow>
				</LegendSection>
			)}

			{jetVisible && (
				<LegendSection>
					<ColorScale label="Jet stream · 250 hPa wind" gradient={cJetGradient} ticks={cJetTicks} />
				</LegendSection>
			)}

			<LegendFootnote>
				ECMWF IFS 0.25° analysis and forecast, 3-hourly, 48 h back and ahead. Upper-air shells are lifted above the surface analysis from space. Hover a centre for its tendency, motion and forecast extreme.
			</LegendFootnote>
		</>
	)
}

/** Cased light-on-dark line, like the globe contours */
function IsobarSwatch({ core = "rgb(242, 247, 255)" }: { core?: string }) {
	return (
		<svg width="36" height="12" viewBox="0 0 36 12" aria-hidden="true" className="flex-shrink-0">
			<path d="M1 8 C 9 2, 15 11, 22 6 S 32 3, 35 5" fill="none" stroke="rgb(5, 13, 31)" strokeWidth="3.5" strokeOpacity="0.8" strokeLinecap="round" />
			<path d="M1 8 C 9 2, 15 11, 22 6 S 32 3, 35 5" fill="none" stroke={core} strokeWidth="1.5" strokeLinecap="round" />
		</svg>
	)
}

/** Trough dash beside a ridge zigzag, in the axis amber */
function AxisSwatch() {
	return (
		<svg width="36" height="12" viewBox="0 0 36 12" aria-hidden="true" className="flex-shrink-0">
			<line x1="1" y1="6" x2="7" y2="6" stroke="rgb(255, 184, 82)" strokeWidth="2" strokeLinecap="round" />
			<line x1="11" y1="6" x2="17" y2="6" stroke="rgb(255, 184, 82)" strokeWidth="2" strokeLinecap="round" />
			<polyline points="21,9 25,3 29,9 33,3" fill="none" stroke="rgb(255, 209, 115)" strokeWidth="2" strokeLinejoin="round" />
		</svg>
	)
}

const cColdCss = "rgb(64, 140, 255)"
const cWarmCss = "rgb(255, 77, 64)"
const cOccludedCss = "rgb(184, 102, 242)"

/** Standard chart glyphs: triangles toward warm air, semicircles toward cold */
function FrontSwatch({ kind }: { kind: "cold" | "warm" | "stationary" | "occluded" }) {
	const triangle = (x: number, color: string, up: boolean) => (
		<path
			key={`t${x}`}
			d={up ? `M${x - 4} 7 L${x} 1 L${x + 4} 7 Z` : `M${x - 4} 7 L${x} 13 L${x + 4} 7 Z`}
			fill={color}
		/>
	)
	const semicircle = (x: number, color: string, up: boolean) => (
		<path
			key={`s${x}`}
			d={up ? `M${x - 4} 7 A 4 4 0 0 1 ${x + 4} 7 Z` : `M${x - 4} 7 A 4 4 0 0 0 ${x + 4} 7 Z`}
			fill={color}
		/>
	)

	let glyphs: React.ReactNode[]
	let stroke: React.ReactNode
	if (kind === "cold") {
		stroke = <line x1="0" y1="7" x2="36" y2="7" stroke={cColdCss} strokeWidth="2" />
		glyphs = [triangle(10, cColdCss, true), triangle(26, cColdCss, true)]
	} else if (kind === "warm") {
		stroke = <line x1="0" y1="7" x2="36" y2="7" stroke={cWarmCss} strokeWidth="2" />
		glyphs = [semicircle(10, cWarmCss, true), semicircle(26, cWarmCss, true)]
	} else if (kind === "occluded") {
		stroke = <line x1="0" y1="7" x2="36" y2="7" stroke={cOccludedCss} strokeWidth="2" />
		glyphs = [triangle(10, cOccludedCss, true), semicircle(26, cOccludedCss, true)]
	} else {
		stroke = (
			<>
				<line x1="0" y1="7" x2="18" y2="7" stroke={cColdCss} strokeWidth="2" />
				<line x1="18" y1="7" x2="36" y2="7" stroke={cWarmCss} strokeWidth="2" />
			</>
		)
		glyphs = [triangle(10, cColdCss, true), semicircle(26, cWarmCss, false)]
	}

	return (
		<svg width="36" height="14" viewBox="0 0 36 14" aria-hidden="true" className="flex-shrink-0">
			{stroke}
			{glyphs}
		</svg>
	)
}
